import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from "axios";
import { queryClient } from "utils/queryClient";
import { storedTokens } from "utils/storedTokens";
import { T_Api_Success_Res } from "./api.types";
import store from "store";
import { resetUser } from "store/userSlice";

export const API_URL = import.meta.env.VITE_APP_API_URL;
export const GETCLEAR_URL = import.meta.env.VITE_APP_GETCLEAR_API_URL;

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 10 * 60 * 1000,
  headers: {
    "x-api-key": import.meta.env.VITE_X_API_KEY,
  },
});

export const getClearApi: AxiosInstance = axios.create({
  baseURL: GETCLEAR_URL,
  timeout: 10 * 60 * 1000,
});

function logout() {
  storedTokens.accessToken.removeToken();
  storedTokens.refreshToken.removeToken();
  store.dispatch(resetUser());
  window.location.replace("/");
  queryClient.clear();
}

function refresh() {
  return new Promise((resolve, reject) => {
    api
      .post(`/user/refresh-token`, { refreshToken: storedTokens.refreshToken.getToken() })
      .then((res) => {
        const data = res.data as T_Api_Success_Res<{ accessToken: string }>;
        storedTokens.accessToken.setToken(data.result.accessToken);
        return resolve(data.result.accessToken);
      })
      .catch(async (error) => {
        logout();
        return reject(error.response?.data?.message);
      });
  });
}

api.interceptors.request.use(
  (config) => {
    const token = storedTokens.accessToken.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;

    const message = error.response?.data?.message;

    if (
      error.response?.status === 401 &&
      ((message as string).toLowerCase() === "session terminated".toLowerCase() ||
        (message as string).toLowerCase() === "Session expired, please login again".toLowerCase())
    ) {
      logout();
      return Promise.reject(error);
    }

    const shouldRefresh =
      error.response?.status === 401 &&
      (message === "Expired or Invalid Token" || message === "Authentication Header Not Found");

    if (shouldRefresh && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!storedTokens.refreshToken.getToken()) {
        logout();
        return Promise.reject(error);
      }

      try {
        const newToken = await refresh();
        error.config.headers.Authorization = `Bearer ${newToken}`;
        return await api(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export { api };
